import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material'
import { Camera01Icon } from '@untitled-theme/icons-react'
import { useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { useUploadBuildingPhotoWithProgress } from '../../api/building-photos.queries'

interface PhotoUploaderProps {
  buildingUuid: string
  currentImageUrl: string | null
  fallbackImageUrl: string
  onUploadSuccess?: () => void
}

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic']
const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20 MB

export const PhotoUploader = ({
  buildingUuid,
  currentImageUrl,
  fallbackImageUrl,
  onUploadSuccess,
}: PhotoUploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const uploadMutation = useUploadBuildingPhotoWithProgress({
    onSuccess: () => {
      toast.success('Foto erfolgreich hochgeladen', {
        autoClose: 3000,
      })
      setUploadProgress(null)
      setIsUploading(false)
      onUploadSuccess?.()
    },
    onError: (error: Error) => {
      toast.error(`Fehler beim Hochladen des Fotos: ${error.message}`, {
        autoClose: 5000,
      })
      setUploadProgress(null)
      setIsUploading(false)
    },
  })

  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click()
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error('Ungültiger Dateityp. Erlaubte Typen: JPG, PNG, WebP, HEIC', {
        autoClose: 5000,
      })
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error('Die Datei ist zu groß. Maximale Größe: 20 MB', {
        autoClose: 5000,
      })
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    uploadMutation.mutate({
      file,
      buildingUuid,
      onProgress: progress => {
        setUploadProgress(progress)
      },
    })

    // Reset input so the same file can be selected again
    event.target.value = ''
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        cursor: isUploading ? 'default' : 'pointer',
        '&:hover .upload-overlay': {
          opacity: isUploading ? 0 : 1,
        },
      }}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.heic"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <Box
        component="img"
        src={currentImageUrl || fallbackImageUrl}
        alt="Building"
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          if (e.currentTarget.src !== fallbackImageUrl) {
            e.currentTarget.src = fallbackImageUrl
          }
        }}
        sx={{
          width: '100%',
          height: { xs: 'auto', sm: '100%' },
          objectFit: 'cover',
          borderRadius: '12px',
          display: 'block',
        }}
      />

      <Box
        className="upload-overlay"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out',
        }}
      >
        <Tooltip title="Foto hochladen" arrow>
          <IconButton
            sx={{
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <Camera01Icon style={{ width: 24, height: 24, color: '#181D29' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {isUploading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <CircularProgress
            variant={uploadProgress !== null ? 'determinate' : 'indeterminate'}
            value={uploadProgress ?? undefined}
            sx={{ color: 'white' }}
          />
          {uploadProgress !== null && (
            <Box
              sx={{
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {uploadProgress}%
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
